import type { Dispatcher } from '../dispatcher.js';
import { ToolError } from '../dispatcher.js';
import { ERROR_CODES } from '../wsClient.js';
import { serializeSession } from '../serializer.js';
import { nowIso, store, getStudioContext, type PlaceholderMarker } from './store.js';

interface ListedMarker {
  id: string;
  name: string;
  position_seconds: number;
  color?: string;
}

interface ListMarkersResult {
  markers: ListedMarker[];
}

interface AddMarkerParams {
  name: string;
  position_seconds: number;
  color?: string;
}
interface AddMarkerResult {
  marker: ListedMarker;
  applied_at: string;
}

// When a studio context is attached we route through `serializeSession` so
// that openDAW's marker shape (`MarkerBoxAdapter` exposes `label`/`position`
// in PPQN) gets normalised to `time_seconds`. We then re-map to the §5.1
// marker schema (`position_seconds`).
function listMarkers(): ListMarkersResult {
  const ctx = getStudioContext();
  if (ctx === null) {
    return { markers: store.getProject().markers.map(toListedMarker) };
  }
  const session = serializeSession(ctx.getProject());
  return {
    markers: session.markers.map((m) => ({
      id: m.id,
      name: m.name,
      position_seconds: m.position_seconds,
      ...(typeof m.color === 'string' ? { color: m.color } : {}),
    })),
  };
}

function toListedMarker(m: PlaceholderMarker): ListedMarker {
  return {
    id: m.id,
    name: m.name,
    position_seconds: m.position_seconds,
    ...(typeof m.color === 'string' ? { color: m.color } : {}),
  };
}

function addMarker(params: AddMarkerParams): AddMarkerResult {
  if (
    !params ||
    typeof params.name !== 'string' ||
    typeof params.position_seconds !== 'number'
  ) {
    throw new ToolError(
      ERROR_CODES.INVALID_PARAMS,
      'addMarker: name and position_seconds required',
    );
  }
  if (!Number.isFinite(params.position_seconds) || params.position_seconds < 0) {
    throw new ToolError(ERROR_CODES.INVALID_PARAMS, 'position_seconds must be >= 0');
  }
  const ctx = getStudioContext();
  if (ctx !== null && typeof ctx.addMarker === 'function') {
    // The adapter (openDawAdapter.makeStudioContext) converts seconds -> PPQN
    // via the project's current bpm and dispatches to an injected
    // `addMarkerDirect` that builds a real MarkerBox inside `editing.modify`.
    const created = ctx.addMarker({
      name: params.name,
      position_seconds: params.position_seconds,
      color: params.color,
    });
    return {
      marker: {
        id: created.id,
        name: created.name,
        position_seconds: created.position_seconds,
        ...(typeof created.color === 'string' ? { color: created.color } : {}),
      },
      applied_at: nowIso(),
    };
  }
  const marker: PlaceholderMarker = {
    id: `marker-${Math.random().toString(36).slice(2, 10)}`,
    name: params.name,
    position_seconds: params.position_seconds,
    color: params.color,
  };
  store.getProject().markers.push(marker);
  return { marker: toListedMarker(marker), applied_at: nowIso() };
}

export const markerHandlers = { listMarkers, addMarker } as const;

export function registerMarkerHandlers(d: Dispatcher): void {
  d.register('listMarkers', listMarkers);
  d.register<AddMarkerParams, AddMarkerResult>('addMarker', addMarker);
}
